import React, {
  useEffect,
  useState,
  createContext,
  useRef,
  useContext,
} from "react";

import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import PageVisibility from "react-page-visibility";
import { useToast } from "../components/ui/Toast";

import {
  getStorageKey,
  setStorageKey,
  removeStorageKey,
  clearStorage,
} from "../core/utils/storage";
import { DEVICE_ID_KEY, REFRESH_TOKEN_KEY } from "../core/utils/deviceId";
import { Window } from "./index.jsx";
import { useAddToCart } from '../cart/index.jsx';
import { getApiUrl } from '../core/config/api.js';
import { showApiError } from '../hooks/useApiError.js';
import { interceptorPolicy } from '../core/errors/interceptorPolicy.js';
import { queryClient } from '../core/queryClient.js';
import {
  getStoredActiveStoreId,
  persistActiveStoreId,
  pickDefaultStoreId,
  clearActiveStore,
} from '../core/config/activeStore.js';

const POLLING_INTERVAL = 31 * 1000;

// Helper function to get query parameter from URL
const queryParam = (name) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export const UserContext = createContext();

/**
 * Derive the client-side role convenience flags on a user object.
 *
 * ⚠️ `is_dao_admin` is a MISLEADING legacy name. It means nothing more than
 * "an admin who is not a marketplace merchant" — it is NOT "Retail Circle /
 * DAO Watches staff". Every tenant store admin gets it. Historically that made
 * it leak RC-internal controls (background removal, price analytics, global
 * product editing) to merchant store admins.
 *
 * `is_internal_admin` is the honest alias for the same predicate. New code
 * should read that name; existing call sites still read `is_dao_admin` and can
 * be migrated opportunistically (both keys are always set together, so either
 * is safe). The old key is deliberately kept — the user object is persisted to
 * localStorage, so dropping it would break the first paint of every session
 * that was established before the deploy.
 *
 * If what you actually need is "RC/DAO staff only", use `is_master_admin` or
 * `isRcOrDaoStaff()` from core/config/storeId.js instead of either flag.
 */
const deriveRoleFlags = (user) => {
  if (user?.is_admin) {
    if (user.is_merchant) {
      user.is_merchant_admin = true;
    } else {
      user.is_dao_admin = true;
      user.is_internal_admin = true;
    }
  }

  return user;
};

const defaultUser = () => {
  try {
    // Re-derive on hydrate as well as on update(), so the flags are present on
    // the very first paint (before refreshContexts() round-trips) even for a
    // user object that was persisted by an older build.
    return deriveRoleFlags(JSON.parse(getStorageKey("user")));
  } catch (error) {
    return null;
  }
};

const UserContextProvider = (props) => {
  const toast = useToast();
  const history = useHistory();
  const [token, setToken] = useState(getStorageKey("token"));
  const [shopifyAccessToken] = useState(getStorageKey("shopifyAccessToken"));
  const [merchantUuid] = useState(queryParam("merchant") || null);
  const [shopifyToken] = useState(queryParam("shopify") || null);
  const [anonClientContext] = useState(
    queryParam("anon_client_context") === "yes"
  );
  const [user, setUser] = useState(defaultUser());
  const [notifications, setNotifications] = useState({});
  const [activeStoreId, setActiveStoreIdState] = useState(getStoredActiveStoreId());
  const [accessibleStores, setAccessibleStores] = useState([]);
  const pollingRef = useRef(null);
  // The axios interceptor is registered once on mount, so it can only ever see
  // the mount-time `user`/`token` closure. These refs give it the live values —
  // without them a session established after mount looks logged-out to the
  // 401 handler, and a session restored at mount looks logged-in forever.
  const userRef = useRef(user);
  const tokenRef = useRef(token);
  // Single-flight guard: a page load fires many requests at once, and a burst
  // of 401s must trigger exactly one refresh, not one per request.
  const refreshInFlightRef = useRef(null);
  const refreshSessionRef = useRef(null);
  // Latched once the server has definitively rejected the stored refresh
  // token, so the other in-flight 401s don't each re-ask the same question.
  const refreshRejectedRef = useRef(false);

  userRef.current = user;
  tokenRef.current = token;

  useEffect(() => {
    context.notifications = notifications;
    setContext({ ...context });
  }, [notifications]);

  if (user && token && !anonClientContext) {
    axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  } else {
    axios.defaults.headers.common["Authorization"] = "";
  }

  if (Window.REFERRAL_CODE) {
    const storedReferralCode = getStorageKey("referralCode");
    const newReferralCode = Window.REFERRAL_CODE;
    const newReferralType = Window.REFERRAL_TYPE;

    // 2026-04-21 finding F1
    setStorageKey("referralCode", newReferralCode);
    setStorageKey("referralType", newReferralType);

    if (storedReferralCode !== newReferralCode && user) {
      axios.put(getApiUrl("/api/referral-codes/" + newReferralCode + "/users/" + user.id));
    }
  }

  const refreshContexts = async () => {
    if (shopifyAccessToken) {
      try {
        const { data } = await axios.get(getApiUrl(`/api/user`), {
          headers: {
            Authorization: "Bearer " + shopifyAccessToken,
          },
          metadata: { silent: true },
        });

        if (!data) {
          return;
        }

        updateToken(shopifyAccessToken);
        update(data);
        return;
      } catch (err) {
        // Do nothing
      }
    }

    if (!user) {
      return;
    }

    const { data } = await axios.get(getApiUrl(`/api/users/${user.id}`), { metadata: { silent: true } });

    if (!user || !token) {
      // prevent update immediately after logout
      return;
    }

    update(data.user);

    if (data.token) {
      updateToken(data.token);
    }
  };

  const handleVisibilityChange = async (isVisible) => {
    if (!isVisible || !user || !token) {
      return;
    }

    await refreshContexts();
  };

  const fetchNotifications = async (user) => {
    if (!user) {
      return;
    }

    const { data } = await axios.get(getApiUrl(`/api/users/${user.id}/notifications`), { metadata: { silent: true } });

    setNotifications((old) => {
      return JSON.stringify(data) !== JSON.stringify(old) ? data : old;
    });
  };

  const startPolling = () => {
    if (pollingRef.current || !user) {
      return;
    }

    fetchNotifications(user);

    pollingRef.current = setInterval(() => {
      fetchNotifications(user);
    }, POLLING_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const seeNotification = async (notification) => {
    if (!user) {
      return;
    }

    await axios.put(getApiUrl(`/api/users/${user.id}/notifications`), {
      notifications: Array.isArray(notification)
        ? notification
        : [notification],
    });
    fetchNotifications(user);
  };

  useEffect(() => {
    refreshContexts();

    startPolling();

    return () => {
      stopPolling();
    };
  }, []);

  const updateToken = (token) => {
    setToken(token);
    setStorageKey("token", token);

    context.token = token;
    setContext({ ...context });
  };

  const update = (user) => {
    if (anonClientContext) {
      return;
    }

    deriveRoleFlags(user);

    setUser(user);
    setStorageKey("user", JSON.stringify(user));

    context.user = user;
    setContext({ ...context });
  };

  /**
   * Trade the stored refresh token for a fresh access token.
   *
   * RC sign-in hands back a 30-day refresh token alongside the access token;
   * before this it was thrown away, so there was no way back from a dead
   * access token — the first 401 ran clearStorage() and dumped the user on
   * /sign-in. Resolves to the new access token, or null when no silent
   * recovery is possible (no stored refresh token — e.g. a DAO
   * email/password session — or the token was expired/revoked/rotated).
   */
  // The Authorization header a request actually went out with. axios 1.x hands
  // back an AxiosHeaders instance on error.config; older shapes are plain
  // objects, so support both.
  const sentAuthHeader = (config) => {
    const headers = config?.headers;

    if (!headers) {
      return "";
    }

    if (typeof headers.get === "function") {
      return headers.get("Authorization") || headers.get("authorization") || "";
    }

    return headers.Authorization || headers.authorization || "";
  };

  const refreshSession = () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    if (refreshRejectedRef.current) {
      return Promise.resolve(null);
    }

    const refreshToken = getStorageKey(REFRESH_TOKEN_KEY);
    // Read the id, never mint one: the backend matches refresh_token AND
    // device_id, so a freshly minted id could never redeem a stored token.
    const deviceId = getStorageKey(DEVICE_ID_KEY);

    if (!refreshToken || !deviceId) {
      return Promise.resolve(null);
    }

    refreshInFlightRef.current = axios
      .post(
        getApiUrl("/app_api/rc-users/refresh-token"),
        { refreshToken, deviceId },
        {
          // silent: keep this out of the error-toast/logout path below — a
          // failed refresh is handled by the caller, not surfaced directly.
          metadata: { silent: true },
          // The dead bearer must not ride along on a public endpoint.
          headers: { Authorization: "" },
        }
      )
      .then(({ data }) => {
        if (!data?.success || !data?.access_token) {
          refreshRejectedRef.current = true;
          return null;
        }

        // Refresh tokens are single-use — the backend revokes the old row and
        // issues a new one, so this MUST be stored or the next refresh fails.
        setStorageKey(REFRESH_TOKEN_KEY, data.refresh_token);
        updateToken(data.access_token);

        // Render re-applies this from state, but requests that fire before the
        // re-render would otherwise still carry the dead token.
        if (!anonClientContext) {
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + data.access_token;
        }

        return data.access_token;
      })
      .catch((error) => {
        // The backend answers a dead/rotated refresh token with 400, which is
        // final — latch it. A network blip or 5xx is not, so leave that open
        // for a later request to retry.
        const refreshStatus = error?.response?.status;

        if (refreshStatus >= 400 && refreshStatus < 500) {
          refreshRejectedRef.current = true;
        }

        return null;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });

    return refreshInFlightRef.current;
  };

  // Re-pointed every render so the mount-time interceptor calls the current
  // closure — `updateToken` captures `context`, which is replaced on each
  // update, and a stale one would roll other context fields back.
  refreshSessionRef.current = refreshSession;

  const { addToCart } = useAddToCart();

  const updateCart = async (user) => {
    const itemsLocalStorage = localStorage.getItem("cart");
    if (itemsLocalStorage) {
      const items = JSON.parse(itemsLocalStorage);
      await addToCart({
        items,
        clientId: user.id,
      });
      localStorage.removeItem("cart");
    }
  };

  const login = (user, token, doRedirect = true, routeState) => {
    if (anonClientContext) {
      return;
    }

    updateToken(token);
    update(user);

    setTimeout(() => {
      updateCart(user);
    }, 3000);

    if (!doRedirect) {
      return;
    }

    startPolling();

    let redirect = getStorageKey("afterLoginRedirect");
    removeStorageKey("afterLoginRedirect");

    const isRetailCircle =
      window.location.hostname.includes("retail-circle") ||
      (import.meta.env.VITE_API_URL || "").includes("retail-circle");

    if (!redirect || redirect[0] !== "/") {
      redirect = isRetailCircle ? "/requests/orders" : "/profile";
    }

    const shopifyParm = shopifyToken ? `shopify=${shopifyToken}` : "";
    const merchantParm = merchantUuid ? `merchant=${merchantUuid}` : "";
    const queryParams = [shopifyParm, merchantParm].filter((p) => p).join("&");

    history.push(redirect + `?${queryParams}`, routeState);
  };

  const logout = () => {
    const refreshToken = getStorageKey(REFRESH_TOKEN_KEY);

    if (refreshToken) {
      // Kill the server-side refresh token too, so the remaining 30-day window
      // can't be replayed. `keepalive` because the location.href below tears
      // the page down immediately — an axios call would just get aborted.
      // Best-effort: clearStorage() already makes this browser sign in again.
      try {
        fetch(getApiUrl("/app_api/rc-users/revoke-refresh-token"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ refreshToken }),
          keepalive: true,
        }).catch(() => {});
      } catch (error) {
        // Ignore — logging out locally is what actually matters.
      }
    }

    // Drops token, user, refreshToken and deviceId — an explicit log out means
    // the next visit in this browser has to sign in again.
    clearStorage();
    clearActiveStore();
    stopPolling();
    location.href = "/";
  };

  // Phase 5 of the multi-store access rework — the stores this user can
  // personally switch into (GET /api/stores/accessible, backed by
  // store_user membership; distinct from GET /api/stores, which returns
  // every store under the caller's SUBSCRIBER regardless of personal
  // access). Best-effort: a failure here just means the switcher has no
  // options, never a hard error.
  const fetchAccessibleStores = async () => {
    if (!user) {
      return;
    }

    try {
      const { data } = await axios.get(getApiUrl(`/api/stores/accessible`), {
        metadata: { silent: true },
      });
      const stores = Array.isArray(data) ? data : [];
      setAccessibleStores(stores);
      context.accessibleStores = stores;
      setContext({ ...context });
      adoptDefaultStore(stores);
    } catch (error) {
      // Do nothing — switcher just renders with no options.
    }
  };

  /**
   * Land on the user's DEFAULT store when they haven't chosen one in this
   * browser (fresh login, new device, cleared storage).
   *
   * The DATA is already correct without this: the backend applies the same
   * default itself when no X-Active-Store arrives
   * (App\Http\Middleware\ResolveActiveStore → User::resolvedDefaultStoreId),
   * so this is purely about the UI agreeing with it. Without it the app knows
   * activeStoreId === null and draws the unscoped state — the "Master Store"
   * banner, the reduced sidebar, and the write guard blocking every create —
   * over data that is in fact scoped to one store. That mismatch is worse than
   * either state on its own.
   *
   * Because the two agree, this deliberately does NOT reload or clear the
   * query cache the way setActiveStore() must: nothing already fetched becomes
   * stale, since every request so far was already scoped to this same store.
   * A reload here would also risk a loop if localStorage writes are blocked.
   */
  const adoptDefaultStore = (stores) => {
    const id = pickDefaultStoreId(stores);

    if (id === null) {
      return;
    }

    persistActiveStoreId(id);
    setActiveStoreIdState(id);
    context.activeStoreId = id;
    setContext({ ...context });
  };

  useEffect(() => {
    fetchAccessibleStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /**
   * Switch the active store. Validated server-side on every subsequent
   * request (App\Http\Middleware\ResolveActiveStore, 403s on anything the
   * caller doesn't actually have store_user access to) — this can only ever
   * narrow/redirect real access, never grant new access.
   *
   * Purges EVERY cache, per the plan's own warning: this is where the
   * visible bugs would be otherwise (stale per-store data lingering after a
   * switch). A full reload — same pattern logout() already uses — is the
   * only way to guarantee that for component-local state too, not just
   * React Query's cache.
   */
  const setActiveStore = (storeId) => {
    const normalised = storeId ? Number(storeId) : null;
    persistActiveStoreId(normalised);
    setActiveStoreIdState(normalised);
    context.activeStoreId = normalised;
    setContext({ ...context });
    queryClient.clear();
    location.reload();
  };

  const [context, setContext] = useState({
    user: anonClientContext ? null : user,
    token: anonClientContext ? null : token,
    refresh: refreshContexts,
    update,
    updateToken,
    login,
    logout,
    seeNotification,
    activeStoreId,
    accessibleStores,
    setActiveStore,
  });

  /**
   * Every API failure goes through the classifier: it decides what the user is
   * told, records the occurrence in Monitoring with the ref shown on screen,
   * and guarantees no backend exception text reaches the page. See
   * src/core/errors/ — this interceptor used to print `Server error (500):
   * <backend message>`, which is how a libcurl error ended up on the Orders
   * page in September 2026.
   */
  const handleApiError = (error, options) => showApiError(toast, error, undefined, options);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(undefined, function(error) {
      const status = error?.response?.status;
      const requestUrl = error?.config?.url || error?.response?.config?.url || '';
      const isSilent = error?.config?.metadata?.silent || requestUrl.includes('/api/log/errors');

      // 401 — try to silently re-establish the session from the stored refresh
      // token and replay the request, and only fall back to logging the user
      // out when that genuinely fails. Deliberately ahead of the isSilent
      // short-circuit: the boot-time refreshContexts() call is silent and is
      // usually the FIRST request to see a dead token, so it has to be able to
      // recover too. For silent requests only the toast is suppressed.
      if (status === 401 && userRef.current && tokenRef.current) {
        const original = error.config || {};

        const giveUp = () => {
          if (isSilent) {
            throw error;
          }

          handleApiError(error);
          setTimeout(() => logout(), 1500);
        };

        // One retry per request — never loop if the new token 401s as well.
        // A staff-only refusal is about the account, not the token: a refresh
        // would be refused too, so sign out straight away.
        const staffOnly = error?.response?.data?.code === "rc_web_staff_only";

        // Except on the invite page: an invitee who is not staff yet may only
        // accept their invite (backend RcWebAccess::inviteeMayCall), so the
        // app's other start-up calls (entitlements, onboarding, …) are refused
        // there by design. Signing them out would make accepting impossible.
        if (staffOnly && window.location.pathname.startsWith("/invite/")) {
          throw error;
        }

        if (original._authRetried || staffOnly) {
          return giveUp();
        }

        original._authRetried = true;

        // A page load fires a dozen requests at once. Once one of them has
        // refreshed the session, the rest 401 with a token that is already
        // stale — replay those against the live token instead of burning
        // another refresh. Refresh tokens are single-use server-side, so
        // without this each in-flight request rotates the token again.
        const currentAuth = tokenRef.current ? "Bearer " + tokenRef.current : "";
        const sentAuth = sentAuthHeader(original);

        if (currentAuth && sentAuth && sentAuth !== currentAuth) {
          original.headers = {
            ...(original.headers || {}),
            Authorization: currentAuth,
          };

          return axios(original);
        }

        return refreshSessionRef.current().then((newToken) => {
          if (!newToken) {
            return giveUp();
          }

          original.headers = {
            ...(original.headers || {}),
            Authorization: "Bearer " + newToken,
          };

          return axios(original);
        });
      }

      if (isSilent) {
        throw error;
      }

      const { notify, swallow } = interceptorPolicy({
        status,
        method: error?.config?.method,
        quiet: Boolean(error?.config?.metadata?.quiet),
      });

      handleApiError(error, { notify });

      if (swallow) {
        return;
      }

      throw error;
    });

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  return (
    <PageVisibility onChange={handleVisibilityChange}>
      <UserContext.Provider value={context}>
        {props.children}
      </UserContext.Provider>
    </PageVisibility>
  );
};

export default UserContextProvider;
